/**
 * useLiveStreamingChat — live-session chat orchestration hook.
 *
 * Owns the live chat overlay's message list, pagination cursor, realtime
 * subscription lifecycle, optimistic text send + by-id rollback, the
 * internet-required send guard, the unread bucket + pill flag + jump-to-bottom
 * state, scroll tracking, and tab-visibility re-raise. Presentation-layer only —
 * it stays UI-free (no `toast.*`, no `Navigator.*`); it surfaces memoized
 * callbacks the page wires and calls `onOfflineSend` so the PAGE fires the
 * offline toast.
 *
 * Modeled on `useCommunity` (`@/presentation/feature/community/hooks/useCommunity`,
 * `presentation.md` § Page + feature-hook split) but MINUS the concerns a live
 * session has none of: NO access gate, NO deep-link creator rehydration, NO
 * reply target, NO like, NO media send. A live send is ALWAYS a text message.
 * The `added`/`modified`/`removed` routing is REUSED wholesale from
 * `useCommunityLiveListener` (id-dedupe of the optimistic echo, background-first
 * unread routing, 3 s pill timer are already parity-correct for the live
 * listener's branches).
 *
 * Differences from `useCommunity` worth calling out:
 *  - `sendText` is a SYNCHRONOUS `(text: string) => boolean` (NOT a `Promise`).
 *    It returns `true` only after the send passes ALL three guards — i.e. the
 *    send "proceeded", so the composer (Task 8) may clear the draft — and fires
 *    the Firestore write FIRE-AND-FORGET so the flag returns before the network
 *    round-trip. It returns `false` from each guard with NO state change and NO
 *    input clear. This mirrors Flutter's `addLiveMessage` `return`ing on the
 *    internet guard (`live_streaming_screen.dart:768-771`) BEFORE clearing the
 *    field (`:802`): an offline send toasts and LEAVES the typed draft intact.
 *  - The app-resume unread re-raise (`didChangeAppLifecycleState`,
 *    `live_streaming_screen.dart:120-140`) is realized SOLELY by the
 *    tab-visibility (`useTabVisibility`) re-raise below. The route-return path
 *    (`didPopNext`, `:267-272`) is a Flutter no-op, so this hook exposes NO
 *    `onPageVisible` and the page wires NO `onPopNext` pill re-raise (business
 *    -parity review_0 Should Fix 1).
 *  - By-id rollback of a failed optimistic TEXT message — the DELIBERATE web
 *    divergence mirroring `useCommunity.sendText` (`useCommunity.ts:882-892`):
 *    Flutter leaves the failed message in place (`:816-818` only re-renders);
 *    the web removes it by `id` (not index) since other messages may have
 *    arrived mid-flight. This realizes the task-prompt's "rollback" request.
 *
 * Flutter source of truth:
 *  - `live_streaming_screen.dart:421-445` (`onJoinChannelSuccess`) — the chat
 *    load fires ON CHANNEL CONNECT (`:443-444`), not at init.
 *  - `live_streaming_screen.dart:592-620` (`getMessages`) — pagination,
 *    `_isFirstLoad`, `mostRecentMessage = page[0]` on first load.
 *  - `live_streaming_screen.dart:622-702` (`subscribeToLiveMessages`) — cursor =
 *    `mostRecentMessage?.createdTimestamp ?? now` (`:623-626`); the three
 *    `added` routes (`:643-680`), `modified` (`:681-689`), `removed` (`:690-698`).
 *  - `live_streaming_screen.dart:178-199` (`scrollListener`) — flush at bottom,
 *    paginate near top, set `_isScrolledUp`.
 *  - `live_streaming_screen.dart:760-821` (`addLiveMessage`) — internet guard
 *    (`:768-771`), empty-text guard (`:773-775`), build (`:777-797`), optimistic
 *    insert at 0 (`:800`), the `:768-771` guard returns BEFORE the `:802` clear.
 *  - `live_streaming_screen.dart:120-140` (`didChangeAppLifecycleState`) — the
 *    app-resume unread re-raise; its web analog is the tab-visibility re-raise.
 *  - `live_streaming_screen.dart:267-272` (`didPopNext`) — an empty no-op; NO
 *    route-return re-raise exists.
 *
 * No unit test is mandated for a feature hook (the convention only requires
 * tests for use cases, `useCommunity.ts:65-66`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';
import { useSelector } from 'react-redux';

import type { RootState } from '@/presentation/store';

import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { CommunityChatMessageRemapper } from '@/domain/remappers/community/communityChatMessageRemapper';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';

import { FetchLiveMessagesUseCase } from '@/domain/usecases/liveStreaming/fetchLiveMessagesUseCase';
import { SubscribeToLiveMessagesUseCase } from '@/domain/usecases/liveStreaming/subscribeToLiveMessagesUseCase';
import { AddLiveMessageUseCase } from '@/domain/usecases/liveStreaming/addLiveMessageUseCase';

import { appLogger } from '@/domain/services/analytics/appLogger';
import { sortCommunityMessagesByCreatedDesc } from '@/domain/utils/communityUtils';

import { useInternetConnectivity } from '@/presentation/hooks/useInternetConnectivity';
import { useTabVisibility } from '@/presentation/utils/tabVisibility';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

import { useCommunityLiveListener } from '@/presentation/feature/community/hooks/useCommunityLiveListener';

export interface UseLiveStreamingChatArgs {
  /**
   * The live SESSION id — the `parentId` of every live message. Drives the
   * fetch / subscribe queries and the built message's `parentId`.
   */
  liveStreamingMessageId: string;
  /**
   * The broadcaster's firebaseId — becomes the built message's
   * `messageUsersInfo.owner` (the write target,
   * `live_streaming_screen.dart:785`). Unlike a wall message (owner = the wall
   * creator), a live message's owner is the broadcaster.
   */
  broadcasterFirebaseId: string;
  /**
   * `true` once the Agora channel has opened. Flutter fires the chat load from
   * `onJoinChannelSuccess` (`live_streaming_screen.dart:443-444`), so the
   * initial fetch + subscription MUST wait for the channel — the one-shot load
   * below is gated on this.
   */
  channelConnected: boolean;
  /**
   * Invoked when a send is blocked by the offline guard
   * (`live_streaming_screen.dart:768-771`) so the PAGE can surface the failure
   * UI (toast) — the hook stays free of UI invocations
   * (`presentation.md` § Page + feature-hook split). Must be referentially
   * stable (memoized by the caller).
   */
  onOfflineSend: () => void;
}

export interface UseLiveStreamingChatResult {
  /** Newest-first message list (lower index = newer). Sorted desc by createdTimestamp. */
  messages: CommunityChatMessageEntity[];
  /**
   * True until the first page has been fetched (mirrors Flutter `_isFirstLoad`).
   * Flips false in the initial-load `finally` regardless of outcome.
   */
  isFirstLoad: boolean;
  /** True while a pagination page is in-flight. */
  isFetchingMore: boolean;
  /** True iff a further (older) page exists (mirrors Flutter `_hasMore`). */
  hasMore: boolean;
  /**
   * The list of unread messages currently bucketed (sorted the same way
   * `messages` is). Exposed so the page can format the most-recent unread item
   * into the unread-pill preview. Flushed to `messages` on `onScrolledToBottom`.
   */
  unreadItems: CommunityChatMessageEntity[];
  /**
   * True for ~3 s after every message-while-scrolled-away arrival (mirrors
   * Flutter `_unreadMessageReceived`). The page gates the unread pill on
   * `unreadBannerVisible && unreadItems.length > 0`.
   */
  unreadBannerVisible: boolean;
  /**
   * True iff the user is currently scrolled away from the bottom of the
   * (reversed) list — drives the page's scroll-down button.
   */
  isScrolledUp: boolean;
  /**
   * Monotonically-increasing counter bumped every time `sendText` inserts an
   * optimistic message. The list owner watches this token and snaps the viewport
   * to the top so the just-sent message lands visible (Flutter
   * `_scrollController.animateTo(0, …)`, `live_streaming_screen.dart:805-812`).
   */
  scrollToTopToken: number;
  /**
   * Send a text message. SYNCHRONOUS "proceeded" flag — returns `true` only when
   * the send passed ALL three guards (so Task 8's composer may clear the field),
   * `false` when a guard blocked it (so the composer must LEAVE the draft
   * intact). Guards, in Flutter's order, each `return false` with NO state change:
   *  1. no current user (`live_streaming_screen.dart:763-766`);
   *  2. the internet guard — offline calls `onOfflineSend()` (the PAGE toasts)
   *     and `return`s WITHOUT sending or clearing (`:768-771`), BEFORE the input
   *     clear at `:802`;
   *  3. empty-text (`text.trim() === ''`, `:773-775`).
   * Only after all three pass: build the entity via
   * `CommunityChatMessageRemapper.fromLiveInput`, optimistically insert at index
   * 0 + sort, bump `scrollToTopToken`, and `return true`. The write is fired
   * FIRE-AND-FORGET so the boolean returns before the network round-trip; on
   * `!success` the optimistic message is removed by `id` (rollback, mirroring
   * `useCommunity.sendText`, `useCommunity.ts:882-892`).
   */
  sendText: (text: string) => boolean;
  /**
   * The list owner calls this when the user reaches the bottom of the (reversed)
   * list. Flushes `unreadItems` into `messages` and clears the pill
   * (`live_streaming_screen.dart:182-187`).
   */
  onScrolledToBottom: () => void;
  /**
   * The list owner calls this on the inverse transition (scrolled away from
   * bottom) so the live-listener `added` branch routes subsequent arrivals into
   * the unread bucket (`live_streaming_screen.dart:193-198`).
   */
  onScrolledAway: () => void;
  /** The list owner calls this when the top sentinel intersects ("load older"). */
  onLoadMore: () => void;
  /**
   * `useBasePage.onDispose` target. Tears down the live listener and clears the
   * unread-pill timer (`live_streaming_screen.dart:225-229`).
   */
  onPageDispose: () => void;
}

export const useLiveStreamingChat = ({
  liveStreamingMessageId,
  broadcasterFirebaseId,
  channelConnected,
  onOfflineSend,
}: UseLiveStreamingChatArgs): UseLiveStreamingChatResult => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // --- Use-case instances (one per hook lifetime) ---
  const fetchLiveMessagesUseCase = useMemo(
    () => new FetchLiveMessagesUseCase(),
    [],
  );
  const subscribeToLiveMessagesUseCase = useMemo(
    () => new SubscribeToLiveMessagesUseCase(),
    [],
  );
  const addLiveMessageUseCase = useMemo(() => new AddLiveMessageUseCase(), []);

  // --- Local state ---
  const [messages, setMessages] = useState<CommunityChatMessageEntity[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  // Messages arrived while the user is scrolled away (or the tab is hidden).
  // Held as state so the pill re-renders; flushed into `messages` on
  // `onScrolledToBottom`.
  const [unreadItems, setUnreadItems] = useState<CommunityChatMessageEntity[]>(
    [],
  );
  // Transient flag mirroring Flutter's `_unreadMessageReceived`. Flipped to
  // `true` on every message-while-scrolled-away arrival (tab visible); the
  // listener's 3 s timer flips it back. Reset on scroll-back-to-bottom.
  const [unreadBannerVisible, setUnreadBannerVisible] = useState<boolean>(false);
  // React-state mirror of `isAtBottomRef` (inverted). Drives the scroll-down
  // button render. Mirrors Flutter's `_isScrolledUp`
  // (`live_streaming_screen.dart:193-198`).
  const [isScrolledUp, setIsScrolledUp] = useState<boolean>(false);
  // Monotonic counter — bumped on every optimistic insert in `sendText`. The
  // list owner watches this to snap the viewport to the top (Flutter
  // `_scrollController.animateTo(0, …)`, `live_streaming_screen.dart:805-812`).
  const [scrollToTopToken, setScrollToTopToken] = useState<number>(0);

  // --- Refs read inside async callbacks / the once-bound live listener ---
  const unreadItemsRef = useRef<CommunityChatMessageEntity[]>([]);
  unreadItemsRef.current = unreadItems;

  // Pagination cursor — the `createdTimestamp` of the oldest message in the most
  // recent page (Firestore `startAfter` cursor). Mirrors Flutter
  // `_lastFetchedDocument` semantically.
  const lastTimestampRef = useRef<number | null>(null);

  // Live-listener cleanup handle.
  const liveUnsubscribeRef = useRef<Unsubscribe | null>(null);

  // True while a pagination request is in-flight. A ref (not only state) so
  // `onLoadMore` short-circuits synchronously on rapid re-fires.
  const isFetchingRef = useRef<boolean>(false);

  // True iff the user is currently pinned near the bottom of the list. Read
  // synchronously by the live-listener `added` branch to route arrivals; driven
  // explicitly by `onScrolledToBottom` / `onScrolledAway`.
  const isAtBottomRef = useRef<boolean>(true);

  // Web mirror of Flutter `_appLifecycleState`: `true` when the tab is visible,
  // `false` when hidden. The live-listener `added` branch reads this
  // synchronously to route hidden-tab arrivals to the unread bucket. Defaults
  // `true` under SSR. Kept fresh by `useTabVisibility`.
  const isTabVisibleRef = useRef<boolean>(
    typeof document === 'undefined'
      ? true
      : document.visibilityState === 'visible',
  );

  // Pending 3 s auto-hide timer for `unreadBannerVisible`. Held in a ref so the
  // listener (outside React's render closure) can clear/reschedule it across
  // consecutive arrivals, and the dispose path can cancel it.
  const unreadBannerTimerRef = useRef<number | null>(null);

  // Single-fire guard for the one-shot channel-connected initial load.
  const hasStartedRef = useRef<boolean>(false);

  // --- Internet connectivity (keep `isOnlineRef` fresh for the sync guard) ---
  // `sendText` is synchronous and reads this ref directly, mirroring Flutter's
  // `_internetConnectionActive` flag (`live_streaming_screen.dart:768`).
  const { isOnline } = useInternetConnectivity();
  const isOnlineRef = useRef<boolean>(isOnline);
  isOnlineRef.current = isOnline;

  // --- Live-listener handlers (bound ONCE) ---
  // Reused wholesale from the wall — its id-dedupe reconciles the optimistic
  // echo, its background-first routing + 3 s timer match the live listener's
  // `added` routes (`live_streaming_screen.dart:643-680`).
  const { handleAdded, handleModified, handleRemoved } = useCommunityLiveListener(
    {
      isAtBottomRef,
      isTabVisibleRef,
      setMessages,
      setUnreadItems,
      setUnreadBannerVisible,
      unreadBannerTimerRef,
    },
  );

  // Re-arm the transient unread pill for 3 s, mirroring the listener's timer so
  // there is exactly ONE auto-hide timer (`unreadBannerTimerRef`). Used by the
  // tab-resume branch to replicate the resume half of Flutter's lifecycle
  // handler (`live_streaming_screen.dart:120-140`): when the tab returns and a
  // message arrived while hidden, re-show the pill for 3 s.
  const raiseUnreadPill = useCallback(() => {
    setUnreadBannerVisible(true);
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
    }
    unreadBannerTimerRef.current = window.setTimeout(() => {
      setUnreadBannerVisible(false);
      unreadBannerTimerRef.current = null;
    }, ANIMATION_CONSTANTS.duration.autoHideDelay);
  }, []);

  // --- Tab-visibility (keep `isTabVisibleRef` fresh + resume the pill) ---
  // This is the SOLE analog of the app-foreground `didChangeAppLifecycleState`
  // (`live_streaming_screen.dart:120-140`). The route-return path (`didPopNext`,
  // `:267-272`) is a Flutter no-op, so there is no `onPageVisible` and no
  // `onPopNext` re-raise (business-parity review_0 Should Fix 1).
  useTabVisibility((isVisible) => {
    isTabVisibleRef.current = isVisible;
    if (isVisible && unreadItemsRef.current.length > 0) {
      raiseUnreadPill();
    }
  });

  // Remove a message from the visible list by `id` — the by-id rollback used on
  // a failed optimistic send (other messages may have arrived mid-flight, so an
  // index-based removal is unsafe). Mirrors `useCommunity.sendText`
  // (`useCommunity.ts:890-892`).
  const removeMessageById = useCallback((id: string): void => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // --- Initial load + listener bootstrap (fires on channel connect) ---
  //
  // Mirrors `useCommunity.runInitialLoad` (`useCommunity.ts:678-758`) MINUS the
  // access gate / deep-link rehydration (a live participant is already in the
  // session):
  //  1. Fetch page 1; seed `messages` + `lastTimestampRef` + `hasMore`.
  //  2. Capture the live cursor = page[0].createdTimestamp ?? now
  //     (`live_streaming_screen.dart:623-626`).
  //  3. Start the live listener (`live_streaming_screen.dart:443-444`).
  const runInitialLoad = useCallback(async () => {
    try {
      // --- Step 1: fetch page 1 ---
      const limit = SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION;
      const page = await fetchLiveMessagesUseCase.execute(
        liveStreamingMessageId,
        null,
        limit,
      );

      setMessages(page.messages);
      lastTimestampRef.current = page.lastTimestamp;
      setHasMore(page.hasMore);

      // --- Step 2: live cursor (`live_streaming_screen.dart:623-626`) ---
      const mostRecent = page.messages[0];
      const cursor = mostRecent?.createdTimestamp ?? Date.now();

      // --- Step 3: start the live listener (`live_streaming_screen.dart:444`) ---
      liveUnsubscribeRef.current = subscribeToLiveMessagesUseCase.execute(
        liveStreamingMessageId,
        cursor,
        {
          onAdded: handleAdded,
          onModified: handleModified,
          onRemoved: handleRemoved,
        },
      );
    } catch (error) {
      appLogger.error(
        'Initial live-chat load failed',
        'useLiveStreamingChat.runInitialLoad',
        { error: error as Error },
      );
    } finally {
      // `_isFirstLoad = false` — flips after the first page regardless of outcome
      // so the page can drop its first-load indicator.
      setIsFirstLoad(false);
    }
  }, [
    liveStreamingMessageId,
    fetchLiveMessagesUseCase,
    subscribeToLiveMessagesUseCase,
    handleAdded,
    handleModified,
    handleRemoved,
  ]);

  // One-shot: fire the load once the channel has connected (Flutter runs it from
  // `onJoinChannelSuccess`, `live_streaming_screen.dart:443-444`). Guarded by
  // `hasStartedRef` (set synchronously before the async dispatch) so a StrictMode
  // double-mount or a `channelConnected` re-render cannot double-fire.
  useEffect(() => {
    if (!channelConnected) return;
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    void runInitialLoad();
  }, [channelConnected, runInitialLoad]);

  // --- sendText (`addLiveMessage`, `live_streaming_screen.dart:760-821`) ---
  // SYNCHRONOUS "proceeded" flag — see the JSDoc on the result field.
  const sendText = useCallback(
    (text: string): boolean => {
      // Guard 1: no current user (`live_streaming_screen.dart:763-766`).
      if (!currentUser) return false;

      // Guard 2: internet (`live_streaming_screen.dart:768-771`). Offline → the
      // PAGE toasts (via `onOfflineSend`) and we `return false` WITHOUT sending
      // or clearing — Flutter `return`s here BEFORE the input clear at `:802`,
      // so the composer keeps the typed draft.
      if (!isOnlineRef.current) {
        onOfflineSend();
        return false;
      }

      // Guard 3: empty-text (`live_streaming_screen.dart:773-775`).
      if (text.trim() === '') return false;

      // Build the optimistic entity SYNCHRONOUSLY via the canonical live-input
      // mapper (Task 3 — the build lives in the domain layer, NOT re-implemented
      // here). A live send is always a text message.
      const message = CommunityChatMessageRemapper.fromLiveInput({
        liveStreamingMessageId,
        broadcasterFirebaseId,
        // Flutter sends the raw controller text (`live_streaming_screen.dart:792`)
        // — the trim above is only the empty-guard, not applied to the payload.
        text,
        currentUser,
      });

      // Optimistic insert at index 0 + sort, then snap the viewport to the top
      // (own send) — Flutter's insert (`:800`) → clear (`:802`) → animate (`:805`)
      // ordering. The input clear rides on this `true` (Task 8's composer).
      setMessages((prev) => {
        const next = [message, ...prev];
        next.sort(sortCommunityMessagesByCreatedDesc);
        return next;
      });
      setScrollToTopToken((prev) => prev + 1);

      // Fire-and-forget the write so the boolean returns synchronously (before
      // the network round-trip) — mirrors Flutter, whose caller does
      // `onTap: () async { addLiveMessage(); }` un-awaited (`:1361-1362`) while
      // `addLiveMessage` awaits the write internally (`:814`). On `!success` (or
      // a thrown error) REMOVE the optimistic message by `id` (the DELIBERATE web
      // divergence mirroring `useCommunity.sendText`, `useCommunity.ts:882-892`).
      void (async () => {
        try {
          const { success } = await addLiveMessageUseCase.execute(message);
          if (!success) removeMessageById(message.id);
        } catch (error) {
          appLogger.error(
            'Send live message failed',
            'useLiveStreamingChat.sendText',
            { error: error as Error },
          );
          removeMessageById(message.id);
        }
      })();

      return true;
    },
    [
      currentUser,
      liveStreamingMessageId,
      broadcasterFirebaseId,
      addLiveMessageUseCase,
      onOfflineSend,
      removeMessageById,
    ],
  );

  // --- onLoadMore (pagination, `live_streaming_screen.dart:188-192`) ---
  const onLoadMore = useCallback((): void => {
    if (isFetchingRef.current || !hasMore) return;
    if (lastTimestampRef.current === null) return; // first-load not complete yet
    isFetchingRef.current = true;
    setIsFetchingMore(true);

    void (async () => {
      try {
        const limit = SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION;
        const page = await fetchLiveMessagesUseCase.execute(
          liveStreamingMessageId,
          lastTimestampRef.current,
          limit,
        );

        if (page.messages.length === 0) {
          setHasMore(page.hasMore);
          return;
        }

        // Merge: existing messages are newer (lower index), the new page is older
        // (higher index). Concat + sort desc handles out-of-order arrivals
        // defensively (mirrors `useCommunity.onLoadMore`).
        setMessages((prev) => {
          const next = prev.concat(page.messages);
          next.sort(sortCommunityMessagesByCreatedDesc);
          return next;
        });

        lastTimestampRef.current = page.lastTimestamp;
        setHasMore(page.hasMore);
      } catch (error) {
        appLogger.error(
          'Paginate older live messages failed',
          'useLiveStreamingChat.onLoadMore',
          { error: error as Error },
        );
      } finally {
        isFetchingRef.current = false;
        setIsFetchingMore(false);
      }
    })();
  }, [hasMore, liveStreamingMessageId, fetchLiveMessagesUseCase]);

  // --- onScrolledToBottom (`live_streaming_screen.dart:182-187`) ---
  // Flush unread items into the visible list and clear the pill.
  const onScrolledToBottom = useCallback((): void => {
    isAtBottomRef.current = true;
    setIsScrolledUp(false);

    if (unreadItemsRef.current.length === 0) return;

    const pending = unreadItemsRef.current;
    setMessages((prev) => {
      const next = prev.concat(pending);
      next.sort(sortCommunityMessagesByCreatedDesc);
      return next;
    });
    setUnreadItems([]);
    setUnreadBannerVisible(false);
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
      unreadBannerTimerRef.current = null;
    }
  }, []);

  // --- onScrolledAway (inverse transition, `live_streaming_screen.dart:193-198`) ---
  const onScrolledAway = useCallback((): void => {
    isAtBottomRef.current = false;
    setIsScrolledUp(true);
  }, []);

  // --- onPageDispose (`live_streaming_screen.dart:225-229`) ---
  const onPageDispose = useCallback((): void => {
    if (liveUnsubscribeRef.current) {
      liveUnsubscribeRef.current();
      liveUnsubscribeRef.current = null;
    }
    if (unreadBannerTimerRef.current !== null) {
      window.clearTimeout(unreadBannerTimerRef.current);
      unreadBannerTimerRef.current = null;
    }
  }, []);

  return {
    messages,
    isFirstLoad,
    isFetchingMore,
    hasMore,
    unreadItems,
    unreadBannerVisible,
    isScrolledUp,
    scrollToTopToken,
    sendText,
    onScrolledToBottom,
    onScrolledAway,
    onLoadMore,
    onPageDispose,
  };
};
